import api from './api';

// Chunk uploads can queue behind other concurrent requests on the same
// connection pool, so they need more headroom than the default API timeout.
const UPLOAD_CHUNK_TIMEOUT = Number(import.meta.env.VITE_UPLOAD_CHUNK_TIMEOUT ?? 120000);

// ─── Types ─────────────────────────────────────────────────────────────────

export interface InitiateRequest {
  file_name: string;
  file_size_bytes: number;
  mime_type: string;
  event_id?: string;
}

export interface InitiateResponse {
  session_id: string;
  chunk_size_bytes: number;
  total_chunks: number;
  expires_at: string;
}

export interface ChunkResponse {
  chunk_index: number;
  chunks_received: number;
  chunks_remaining: number;
  total_chunks: number;
}

export interface ChunksListResponse {
  session_id: string;
  total_chunks: number;
  chunks_received: number;
  chunks_remaining: number;
  received: number[];
  missing: number[];
}

export interface CompleteResponse {
  session_id: string;
  asset_id: string;
  status: string;
}

export type UploadStatus = 'uploading' | 'complete' | 'expired' | 'error';
export type ProcessingStatus =
  | null
  | 'pending_cu'
  | 'cu_processing'
  | 'cu_done'
  | 'indexed'
  | 'error';

export interface StatusResponse {
  session_id: string;
  asset_id: string;
  upload_status: UploadStatus;
  processing_status: ProcessingStatus;
  chunks_received: number;
  total_chunks: number;
  error_message: string | null;
}

// ─── Service ───────────────────────────────────────────────────────────────

export const uploadService = {
  /** POST /api/v2/upload/initiate */
  async initiate(req: InitiateRequest): Promise<InitiateResponse> {
    console.log('[uploadService.initiate] POST /api/v2/upload/initiate', req);
    const r = await api.post<InitiateResponse>('/upload/initiate', req);
    return r.data;
  },

  /** PUT /api/v2/upload/{session_id}/chunk/{chunk_index} */
  async uploadChunk(
    sessionId: string,
    chunkIndex: number,
    data: Blob,
    signal?: AbortSignal,
  ): Promise<ChunkResponse> {
    console.log(
      `[uploadService.uploadChunk] PUT /api/v2/upload/${sessionId}/chunk/${chunkIndex}`,
      { bytes: data.size },
    );
    const r = await api.put<ChunkResponse>(
      `/upload/${sessionId}/chunk/${chunkIndex}`,
      data,
      {
        headers: { 'Content-Type': 'application/octet-stream' },
        signal,
        timeout: UPLOAD_CHUNK_TIMEOUT,
      },
    );
    return r.data;
  },

  /** GET /api/v2/upload/{session_id}/chunks */
  async getChunks(sessionId: string): Promise<ChunksListResponse> {
    console.log(`[uploadService.getChunks] GET /api/v2/upload/${sessionId}/chunks`);
    const r = await api.get<ChunksListResponse>(`/upload/${sessionId}/chunks`);
    return r.data;
  },

  /** POST /api/v2/upload/{session_id}/complete */
  async complete(sessionId: string): Promise<CompleteResponse> {
    console.log(`[uploadService.complete] POST /api/v2/upload/${sessionId}/complete`);
    const r = await api.post<CompleteResponse>(`/upload/${sessionId}/complete`);
    return r.data;
  },

  /** GET /api/v2/upload/{session_id}/status */
  async getStatus(sessionId: string): Promise<StatusResponse> {
    console.log(`[uploadService.getStatus] GET /api/v2/upload/${sessionId}/status`);
    const r = await api.get<StatusResponse>(`/upload/${sessionId}/status`);
    return r.data;
  },
};
